import React, { useState, useRef, useEffect, useCallback } from 'react';

interface InlineEditableTextProps {
  value: string;
  onSave: (newValue: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  as?: 'span' | 'div' | 'h3' | 'p';
  multiline?: boolean;
  disabled?: boolean;
}

/**
 * InlineEditableText
 * 
 * A text element that becomes an input when clicked.
 * - Click on text → edit mode
 * - Enter / blur → save
 * - ESC → cancel
 * - Never shows input in default state
 */
const InlineEditableText: React.FC<InlineEditableTextProps> = ({
  value,
  onSave,
  placeholder = 'Klik om te bewerken...',
  className = '',
  inputClassName = '',
  as: Tag = 'span',
  multiline = false,
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Sync tempValue when value prop changes
  useEffect(() => {
    if (!isEditing) {
      setTempValue(value);
    }
  }, [value, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = useCallback(() => {
    if (disabled) return;
    setTempValue(value);
    setIsEditing(true);
  }, [disabled, value]);

  const handleSave = useCallback(() => {
    const trimmed = tempValue.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    }
    setIsEditing(false);
  }, [tempValue, value, onSave]);

  const handleCancel = useCallback(() => {
    setTempValue(value);
    setIsEditing(false);
  }, [value]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  }, [handleSave, handleCancel, multiline]);

  const handleBlur = useCallback(() => {
    handleSave();
  }, [handleSave]);

  // Edit mode
  if (isEditing) {
    const sharedProps = {
      ref: inputRef as any,
      value: tempValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => 
        setTempValue(e.target.value),
      onKeyDown: handleKeyDown,
      onBlur: handleBlur,
      className: `
        w-full px-2 py-1 text-inherit font-inherit
        border border-orange-400 rounded-md
        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent
        bg-white
        ${inputClassName}
      `.trim(),
      placeholder,
    };

    return multiline ? (
      <textarea {...sharedProps} rows={3} />
    ) : (
      <input type="text" {...sharedProps} />
    );
  }

  // Read mode
  const displayValue = value || placeholder;
  const isEmpty = !value;

  return (
    <Tag
      onClick={handleStartEdit}
      className={`
        cursor-pointer
        hover:bg-gray-50 hover:rounded
        transition-colors duration-150
        ${isEmpty ? 'text-gray-400 italic' : ''}
        ${disabled ? 'cursor-default hover:bg-transparent' : ''}
        ${className}
      `.trim()}
      title={disabled ? undefined : 'Klik om te bewerken'}
    >
      {displayValue}
    </Tag>
  );
};

export default InlineEditableText;
